import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { useGetPendingRequestsSummaryQuery } from '../ess/requestApi';

const EVENT = 'employee-requests-seen';
const ADMIN_ROLES = ['admin', 'super_admin', 'hr_admin', 'hr'];

const read = (key: string): string | null => {
    try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key: string, value: string) => {
    try { localStorage.setItem(key, value); } catch { /* private mode etc. - the dot just won't persist */ }
};

/**
 * Drives the dot next to "Employee Requests" in the sidebar.
 *
 * The dot is lit while there is a pending request NEWER than the newest one this
 * person has already seen. "Seen" is recorded (per user, in this browser) when they
 * open the Employee Requests page, as the arrival time of the newest pending request
 * at that moment - a server timestamp, so the comparison never depends on the
 * browser's clock. Opening the page clears the dot; a request that arrives later
 * lights it again, and a request that was already seen but is still undecided does
 * not keep nagging.
 *
 * Only HR/admin roles poll (the endpoint is theirs); everyone else gets `false`.
 */
export function useUnseenRequests() {
    const user = useAppSelector((state) => state.auth.user);
    const roles = ((user as any)?.roles || []).map((r: string) => String(r).toLowerCase());
    const canSee = roles.some((r: string) => ADMIN_ROLES.includes(r));
    const storageKey = `employee-requests:last-seen:${(user as any)?.tenantId || ''}:${(user as any)?.id || ''}`;

    const { data } = useGetPendingRequestsSummaryQuery(undefined, {
        skip: !canSee,
        pollingInterval: 60_000,
        refetchOnMountOrArgChange: true,
    });

    const [lastSeen, setLastSeen] = useState<string | null>(() => read(storageKey));
    useEffect(() => {
        setLastSeen(read(storageKey));
        const onSeen = () => setLastSeen(read(storageKey));
        window.addEventListener(EVENT, onSeen);
        window.addEventListener('storage', onSeen); // another tab opened the page
        return () => {
            window.removeEventListener(EVENT, onSeen);
            window.removeEventListener('storage', onSeen);
        };
    }, [storageKey]);

    const latest = data?.latestPendingAt ?? null;
    const hasUnseen = canSee && !!latest && (!lastSeen || new Date(latest).getTime() > new Date(lastSeen).getTime());

    /** Call when the person is looking at the requests: `latestPendingAt` is the newest pending arrival they can see. */
    const markSeen = useCallback((latestPendingAt: string | null) => {
        if (!latestPendingAt) return;
        write(storageKey, latestPendingAt);
        window.dispatchEvent(new Event(EVENT));
    }, [storageKey]);

    return { hasUnseen, pendingCount: canSee ? (data?.pending ?? 0) : 0, latestPendingAt: latest, markSeen };
}
